import { z } from "zod";

// Type-only: `devices.ts` imports the schema below at runtime, and a value
// import in this direction would close the cycle.
import type { DeviceType } from "./devices";
import type { HardwareChannelTemplate } from "./channels";
import type { LorawanProfile } from "./lorawan";

/**
 * The hardware this platform can actually ingest.
 *
 * It is a registry rather than free text because the ingestion pipeline is
 * not generic: the decoded-payload mapping, the assumed reporting interval
 * and the offline threshold are all tuned to a specific sensor. Letting
 * somebody register a model we have never seen produces a device that looks
 * registered and silently never reports.
 *
 * "Can ingest" is the entry criterion, and it is stricter than "we can buy
 * it". A model belongs here only once `Telemetry` has somewhere to put its
 * readings and the engine knows how to judge them — or, for controllers,
 * once `DeviceChannel` can hold them (ADR-081). The VS370 sat outside
 * this list for exactly that reason until presence got a column and an
 * alerting model of its own (ADR-069); its being here now is a statement
 * about the platform, not about anybody having tested the sensor.
 *
 * This does not contradict ADR-045, which forbade *defaulting* every device
 * to the pilot model. Nothing is defaulted here: the hardware is still
 * stated explicitly at registration. It just has to be hardware that exists
 * as far as this system is concerned.
 *
 * Only writes are constrained. `Device.model` and `Device.manufacturer` stay
 * free-form strings in the response, so a row recorded before a model left
 * the registry still reads back honestly instead of failing validation.
 *
 * Adding hardware means adding an entry here, copying this file to
 * `web/src/contracts`, and giving it a page entry in
 * `web/src/features/hardware/catalogue.ts` — which a test enforces, so the
 * screen cannot fall behind the registry.
 */

/**
 * Temperature this model reports, and whether it also reports humidity.
 *
 * The uplink number is Celsius. Milesight ToolBox can show Fahrenheit on its
 * own screen; that setting does not change the package we store, so the only
 * unit this type carries is Celsius. Absent on hardware that does not measure
 * temperature.
 */
export interface HardwareClimate {
  readonly temperatureUnit: "CELSIUS";
  readonly humidity: boolean;
}

const CELSIUS_AND_HUMIDITY = {
  temperatureUnit: "CELSIUS",
  humidity: true,
} as const satisfies HardwareClimate;

const CELSIUS_ONLY = {
  temperatureUnit: "CELSIUS",
  humidity: false,
} as const satisfies HardwareClimate;

export interface SupportedHardware {
  /** Stable identifier. Shared with the catalogue page. */
  id: string;
  manufacturer: string;
  model: string;
  /**
   * What this hardware measures. Derived from the model rather than chosen,
   * because an EM300-TH reports temperature and humidity whatever a form
   * says it does.
   */
  type: DeviceType;
  /**
   * Climate reported alongside the primary type. A door contact can still
   * carry temperature and humidity; a probe that only measures temperature
   * sets `humidity` to false. Resolved when the device is loaded.
   */
  climate?: HardwareClimate;
  /**
   * Native channels materialised when an IOT_CONTROLLER is registered.
   * Absent (or empty) for mono-metric sensors.
   */
  channelTemplate?: readonly HardwareChannelTemplate[];
  /** Network Server registration profile (ADR-089). */
  lorawan: LorawanProfile;
}

/**
 * Milesight's published default JoinEUI.
 *
 * Confirmed for the EM300-TH by Milesight's user guide and The Things Stack's
 * device page. The other models are left null until their own documentation
 * says the same, so the technician has to enter it rather than inherit a guess.
 */
const MILESIGHT_DEFAULT_JOIN_EUI = "24E124C0002A0001";

/** Device Repository `profile-au915`: LoRaWAN 1.0.3, class A. */
function milesightClassA(
  modelId: string,
  hardwareVersion: string,
  firmwareVersion: string,
  defaultJoinEui: string | null,
): LorawanProfile {
  return {
    repository: { brandId: "milesight-iot", modelId, hardwareVersion, firmwareVersion },
    macVersion: "MAC_V1_0_3",
    phyVersion: "RP001_V1_0_3_REV_A",
    supportsClassC: false,
    defaultJoinEui,
    frequencyPlans: ["AU_915_928_FSB_2"],
  };
}

/**
 * UC300 native I/O from Milesight's official decoder / protocol (ADR-081).
 * Modbus channels are created on first uplink, not at registration.
 */
const UC300_CHANNEL_TEMPLATE: readonly HardwareChannelTemplate[] = [
  { key: "gpio_input_1", kind: "DIGITAL", sortOrder: 10 },
  { key: "gpio_input_2", kind: "DIGITAL", sortOrder: 11 },
  { key: "gpio_input_3", kind: "DIGITAL", sortOrder: 12 },
  { key: "gpio_input_4", kind: "DIGITAL", sortOrder: 13 },
  { key: "gpio_counter_1", kind: "COUNTER", sortOrder: 20 },
  { key: "gpio_counter_2", kind: "COUNTER", sortOrder: 21 },
  { key: "gpio_counter_3", kind: "COUNTER", sortOrder: 22 },
  { key: "gpio_counter_4", kind: "COUNTER", sortOrder: 23 },
  { key: "gpio_output_1", kind: "DIGITAL_OUTPUT", sortOrder: 30 },
  { key: "gpio_output_2", kind: "DIGITAL_OUTPUT", sortOrder: 31 },
  { key: "pt100_1", kind: "TEMPERATURE", unit: "°C", sortOrder: 40 },
  { key: "pt100_2", kind: "TEMPERATURE", unit: "°C", sortOrder: 41 },
  { key: "adc_1", kind: "CURRENT_MA", unit: "mA", sortOrder: 50 },
  { key: "adc_2", kind: "CURRENT_MA", unit: "mA", sortOrder: 51 },
  { key: "adv_1", kind: "VOLTAGE", unit: "V", sortOrder: 60 },
  { key: "adv_2", kind: "VOLTAGE", unit: "V", sortOrder: 61 },
];

export const SUPPORTED_HARDWARE = [
  {
    id: "milesight-em300-th",
    manufacturer: "Milesight",
    model: "EM300-TH",
    type: "TEMPERATURE_HUMIDITY",
    climate: CELSIUS_AND_HUMIDITY,
    lorawan: milesightClassA("em300-th", "V2.2", "1.15", MILESIGHT_DEFAULT_JOIN_EUI),
  },
  {
    id: "milesight-em500-pt100-t200",
    manufacturer: "Milesight",
    model: "EM500-PT100-T200",
    type: "TEMPERATURE",
    climate: CELSIUS_ONLY,
    // The Device Repository model is `em500-pt100` for every probe option.
    // Only the T200 range is registered; T050, T500 and T800 stay out (ADR-095).
    lorawan: milesightClassA("em500-pt100", "V1.x", "2.x", null),
  },
  {
    id: "milesight-em410-rdl",
    manufacturer: "Milesight",
    model: "EM410-RDL",
    type: "DISTANCE_LEVEL",
    lorawan: milesightClassA("em410-rdl", "V1.0", "1.01", null),
  },
  {
    id: "milesight-vs370",
    manufacturer: "Milesight",
    model: "VS370",
    type: "PRESENCE",
    lorawan: milesightClassA("vs370", "V1.0", "v370.1.0.x", null),
  },
  {
    id: "milesight-em300-sld",
    manufacturer: "Milesight",
    model: "EM300-SLD",
    type: "WATER_LEAK",
    climate: CELSIUS_AND_HUMIDITY,
    // The repository lists hardware 2.x (firmware 1.x) and 3.x (firmware 2.x)
    // with the same AU915 profile and codec; the newer pair is registered.
    lorawan: milesightClassA("em300-sld", "3.x", "2.x", null),
  },
  {
    id: "milesight-em300-mcs",
    manufacturer: "Milesight",
    model: "EM300-MCS",
    type: "MAGNETIC_CONTACT",
    climate: CELSIUS_AND_HUMIDITY,
    lorawan: milesightClassA("em300-mcs", "V3.0", "1.2", null),
  },
  {
    id: "milesight-uc300",
    manufacturer: "Milesight",
    model: "UC300",
    type: "IOT_CONTROLLER",
    channelTemplate: UC300_CHANNEL_TEMPLATE,
    // The repository registers the UC300 on `profile-au915-class-ac`.
    lorawan: {
      ...milesightClassA("uc300", "V1.0", "1.x", null),
      supportsClassC: true,
    },
  },
  {
    id: "milesight-ct103",
    manufacturer: "Milesight",
    model: "CT103",
    type: "CURRENT",
    // Optional cable temperature is stored when the uplink carries it, but
    // this model is not marked climate: that reading must not feed the
    // organisation temperature rule (ADR-102). Device Repository profile
    // `ct103`, hardware V1.0, firmware ct103.1.0.x, class A, AU915.
    // JoinEUI stays null until Milesight's guide states the same default
    // the EM300-TH uses.
    lorawan: milesightClassA("ct103", "V1.0", "ct103.1.0.x", null),
  },
] as const satisfies readonly SupportedHardware[];

export type HardwareId = (typeof SUPPORTED_HARDWARE)[number]["id"];

/**
 * Built from the registry rather than written out, so the two cannot drift.
 * The error message names the options: with a handful of supported models,
 * "not a supported model" without saying which are is a dead end.
 */
export const hardwareIdSchema = z.enum(
  SUPPORTED_HARDWARE.map((hardware) => hardware.id) as [HardwareId, ...HardwareId[]],
  {
    message: `Choose supported hardware: ${SUPPORTED_HARDWARE.map(
      (hardware) => `${hardware.manufacturer} ${hardware.model}`,
    ).join(", ")}.`,
  },
);

export function findHardware(id: string): SupportedHardware | undefined {
  return SUPPORTED_HARDWARE.find((hardware) => hardware.id === id);
}

/** Reverse lookup, for describing a device that was recorded before now. */
export function hardwareFor(manufacturer: string, model: string): SupportedHardware | undefined {
  return SUPPORTED_HARDWARE.find(
    (hardware) =>
      hardware.manufacturer.toLowerCase() === manufacturer.trim().toLowerCase() &&
      hardware.model.toLowerCase() === model.trim().toLowerCase(),
  );
}

/**
 * Climate for a loaded device, when its model measures temperature.
 *
 * Unknown or non-climate hardware returns undefined so a door contact that
 * is not in this registry does not grow a humidity row it never reported.
 */
export function climateOf(manufacturer: string, model: string): HardwareClimate | undefined {
  return hardwareFor(manufacturer, model)?.climate;
}
